import React from 'react';
import { useTranslation } from 'react-i18next';

import { colors } from '@/assets/styles/colors';
import { PermissionGuard } from '@/components/layout';
import { IconScout } from '@/components/ui/images/IconScout';
import { Text } from '@/components/ui/text/Text';
import { TABLE_PALETTE, TABLE_ROW_FONT_SIZE } from '@/components/ui/tables/Table.constants';
import { getWeekdayInitial } from '@/utils';


interface JornadaIconButtonProps {
  icon: 'plus' | 'minus' | 'trash';
  onClick: () => void;
  label: string;
  color: string;
  'data-testid': string;
}

const JornadaIconButton: React.FC<JornadaIconButtonProps> = ({ icon, onClick, label, color, 'data-testid': dataTestId }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center justify-center w-5 h-5 rounded-full border transition-colors duration-150"
    style={{ borderColor: color, color, backgroundColor: colors.grays.neutralFF }}
    aria-label={label}
    data-testid={dataTestId}
  >
    <IconScout name={icon} size={12} />
  </button>
);

export interface MarksManagerJornadaCellProps {
  /** Fecha de la jornada, en formato dd/MM/yyyy. */
  date: string;
  /** ASIS-5572 — true cuando la jornada tiene HHEE generadas (pinta la fecha en azul). */
  hasOvertimeHours: boolean;
  /**
   * ASIS-5572 — true cuando la jornada tiene HHAA generadas (pinta la fecha en amarillo, salvo
   * que también haya HHEE).
   */
  hasAbsentHours: boolean;
  /** true para las filas agregadas localmente vía "+"/"-" (muestran papelera en vez de +/-). */
  isDraft: boolean;
  onAddBefore: () => void;
  onAddAfter: () => void;
  onRemoveDraft: () => void;
}

// Celda de la columna "Jornada" de Gestor de Marcas (ASIS-5572): fecha + inicial del día de la
// semana, coloreada según HHEE/HHAA, con acciones de agregar jornada adicional (+/-) en filas
// originales o de eliminarla (papelera) en filas agregadas localmente.
export const MarksManagerJornadaCell: React.FC<MarksManagerJornadaCellProps> = ({
  date,
  hasOvertimeHours,
  hasAbsentHours,
  isDraft,
  onAddBefore,
  onAddAfter,
  onRemoveDraft,
}) => {
  const { t: translate } = useTranslation();

  // AC de ASIS-5572: azul (HHEE) predomina sobre amarillo (HHAA) cuando ambas están presentes.
  // Sin ninguna de las dos (incluyendo el caso sin marcas de entrada/salida), usa el mismo color
  // de texto que el resto de las columnas de la tabla (TABLE_PALETTE.rowText), no negro puro.
  const dateColor = hasOvertimeHours
    ? colors.important.main
    : hasAbsentHours
      ? colors.feedback.alert300
      : TABLE_PALETTE.rowText;

  const weekdayInitial = getWeekdayInitial(date);

  return (
    <div className="flex items-center gap-1" data-testid="marks-manager-jornada-cell">
      {/* Ancho fijo: el texto de la fecha varía unos píxeles de fila a fila (fuente proporcional,
          dígitos de distinto ancho) — sin esto, los íconos "+"/"-"/papelera quedan desalineados
          verticalmente entre filas en vez de formar una columna recta. `Text variant="subheader-
          regular"` + `TABLE_ROW_FONT_SIZE`: misma fuente/tamaño que `resolveCellContent` aplica al
          resto de las celdas de la tabla (ver Table.utils.tsx) y que MarksManager.tsx le pasa a
          `<Table fontSize={TABLE_ROW_FONT_SIZE}>`. Ancho y gap acotados al mínimo para dejarle
          más espacio a la columna RUT (ver useMarksManager.ts). */}
      <div className="w-[110px] shrink-0">
        <Text
          variant="subheader-regular"
          color={dateColor}
          style={{ fontSize: TABLE_ROW_FONT_SIZE }}
          data-testid="marks-manager-jornada-date-text"
        >
          {weekdayInitial ? `${date} (${weekdayInitial})` : date}
        </Text>
      </div>

      {!isDraft && (
        <PermissionGuard action="edit" fallback="hidden">
          <>
            <JornadaIconButton
              icon="minus"
              onClick={onAddBefore}
              label={translate('attendance:marksManager.actions.addDayBefore')}
              color={colors.important.main}
              data-testid="marks-manager-jornada-add-before-button"
            />
            <JornadaIconButton
              icon="plus"
              onClick={onAddAfter}
              label={translate('attendance:marksManager.actions.addDayAfter')}
              color={colors.important.main}
              data-testid="marks-manager-jornada-add-after-button"
            />
          </>
        </PermissionGuard>
      )}

      {isDraft && (
        <PermissionGuard action="edit" fallback="hidden">
          <JornadaIconButton
            icon="trash"
            onClick={onRemoveDraft}
            label={translate('attendance:marksManager.actions.removeDay')}
            color={colors.feedback.error300}
            data-testid="marks-manager-jornada-remove-draft-button"
          />
        </PermissionGuard>
      )}
    </div>
  );
};

export default MarksManagerJornadaCell;
